import React from 'react';
import { joinClassNames } from '../../utils';
import { ProcessedSection } from '../../sectionProcessing';
import sectionStyles from '../../StructuredSections.module.scss';
import { SitePageHtmlContent } from '../SitePageHtmlContent';

interface ParsedRichTableCell {
    html: string;
    isHeader: boolean;
}

interface ParsedRichTableRow {
    cells: ParsedRichTableCell[];
}

interface ParsedRichTable {
    headerRow: string[] | null;
    rows: ParsedRichTableRow[];
    columnCount: number;
    firstColumnIsHeader: boolean;
}

function stripHtml(value: string): string {
    return value.replace(/<[^>]*>/g, '').trim();
}

function extractCellHtml(cell: Element): string {
    return (cell.innerHTML || '').trim();
}

function parseRichTableHtml(html: string): ParsedRichTable | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    const table = template.content.querySelector('table');
    if (!table) {
        return null;
    }

    const theadRow = table.querySelector('thead tr') as HTMLTableRowElement | null;
    let headerRow: string[] | null = null;
    if (theadRow) {
        headerRow = Array.from(theadRow.cells).map((cell) => extractCellHtml(cell));
    }

    let dataRowElements = Array.from(table.querySelectorAll('tbody tr')) as HTMLTableRowElement[];
    if (dataRowElements.length === 0) {
        dataRowElements = Array.from(table.querySelectorAll('tr')) as HTMLTableRowElement[];
    }

    const [firstDataRow] = dataRowElements;
    if (!headerRow && firstDataRow) {
        const firstRowCells = Array.from(firstDataRow.cells);
        const isHeaderRow =
            firstRowCells.length > 0 &&
            firstRowCells.every((cell) => cell.tagName.toLowerCase() === 'th');
        if (isHeaderRow) {
            headerRow = firstRowCells.map((cell) => extractCellHtml(cell));
            dataRowElements = dataRowElements.slice(1);
        }
    }

    const rows: ParsedRichTableRow[] = dataRowElements.map((row) => ({
        cells: Array.from(row.cells).map((cell) => ({
            html: extractCellHtml(cell),
            isHeader: cell.tagName.toLowerCase() === 'th',
        })),
    }));

    const columnCount = Math.max(
        headerRow ? headerRow.length : 0,
        ...rows.map((row) => row.cells.length)
    );

    const firstColumnIsHeader =
        rows.length > 0 &&
        rows.every((row) => row.cells.length > 0 && row.cells[0]?.isHeader === true);

    return {
        headerRow,
        rows,
        columnCount,
        firstColumnIsHeader,
    };
}

export function renderRichTableBlockContent(section: ProcessedSection): React.ReactNode {
    const block = section.richTableBlock;
    if (!block) {
        return null;
    }

    const trimmedCaption = block.caption?.trim() ?? null;
    const hasCaption = Boolean(trimmedCaption);
    const parsed = parseRichTableHtml(block.tableHtml);
    const wrapperClassName = joinClassNames(
        sectionStyles.tableWrapper,
        !hasCaption ? sectionStyles.tableWrapperNoCaption : undefined
    );
    const richContentClassName = joinClassNames(
        sectionStyles.richTableContent,
        parsed ? sectionStyles.richTableContentHiddenOnMobile : undefined
    );

    const hasHeaderRow = Boolean(parsed?.headerRow && parsed.headerRow.length > 0);
    const hasRowHeaders = parsed?.firstColumnIsHeader ?? false;

    const mobileColumns =
        parsed && hasHeaderRow
            ? Array.from({ length: parsed.columnCount }, (_, columnIndex) => columnIndex)
                  .filter((columnIndex) => !(hasRowHeaders && columnIndex === 0))
                  .map((columnIndex) => {
                      const columnHeaderHtml =
                          parsed.headerRow && parsed.headerRow[columnIndex]
                              ? parsed.headerRow[columnIndex]!
                              : '';
                      const columnCells = parsed.rows.map((row) => {
                          const cell = row.cells[columnIndex];
                          return {
                              value: cell ? cell.html : '',
                              rowHeaderHtml:
                                  hasRowHeaders && columnIndex !== 0
                                      ? row.cells[0]?.html ?? ''
                                      : null,
                          };
                      });

                      const hasContent =
                          stripHtml(columnHeaderHtml).length > 0 ||
                          columnCells.some(
                              (cell) =>
                                  stripHtml(cell.value).length > 0 ||
                                  (cell.rowHeaderHtml && stripHtml(cell.rowHeaderHtml).length > 0)
                          );

                      if (!hasContent) {
                          return null;
                      }

                      return {
                          columnHeaderHtml,
                          columnCells,
                          index: columnIndex,
                      };
                  })
                  .filter(
                      (
                          column
                      ): column is {
                          columnHeaderHtml: string;
                          columnCells: { value: string; rowHeaderHtml: string | null }[];
                          index: number;
                      } => column !== null
                  )
            : null;

    const mobileRows =
        parsed && !hasHeaderRow && hasRowHeaders
            ? parsed.rows
                  .map((row, rowIndex) => {
                      const rowHeaderHtml = row.cells[0]?.html ?? '';
                      const cells = row.cells.slice(1).map((cell) => cell?.html ?? '');
                      const hasVisibleContent =
                          stripHtml(rowHeaderHtml).length > 0 ||
                          cells.some((cell) => stripHtml(cell).length > 0);
                      if (!hasVisibleContent) {
                          return null;
                      }
                      return {
                          rowHeaderHtml,
                          cells,
                          index: rowIndex,
                      };
                  })
                  .filter(
                      (
                          row
                      ): row is {
                          rowHeaderHtml: string;
                          cells: string[];
                          index: number;
                      } => row !== null
                  )
            : null;

    const mobileFallbackRows =
        parsed && !mobileColumns && !mobileRows
            ? parsed.rows
                  .map((row, rowIndex) => {
                      const cells = row.cells.map((cell) => cell?.html ?? '');
                      const hasVisibleContent = cells.some((cell) => stripHtml(cell).length > 0);
                      if (!hasVisibleContent) {
                          return null;
                      }
                      return {
                          index: rowIndex,
                          cells,
                      };
                  })
                  .filter(
                      (
                          row
                      ): row is {
                          index: number;
                          cells: string[];
                      } => row !== null
                  )
            : null;

    return (
        <div className={wrapperClassName}>
            {hasCaption ? (
                <div className={sectionStyles.tableCaptionMobile}>{trimmedCaption}</div>
            ) : null}
            <SitePageHtmlContent as="div" className={richContentClassName} html={block.tableHtml} />
            {mobileColumns ? (
                <div className={sectionStyles.tableMobile}>
                    {mobileColumns.map((column) => {
                        if (!column) {
                            return null;
                        }
                        const columnHeaderHtml = column.columnHeaderHtml;
                        return (
                            <div
                                key={`rich-column-${column.index}`}
                                className={sectionStyles.tableMobileColumn}
                            >
                                {stripHtml(columnHeaderHtml).length > 0 ? (
                                    <SitePageHtmlContent
                                        as="div"
                                        className={sectionStyles.tableMobileColumnHeader}
                                        html={columnHeaderHtml}
                                    />
                                ) : null}
                                {column.columnCells.map((cell, rowIndex) => {
                                    const hasRowHeader =
                                        cell.rowHeaderHtml &&
                                        stripHtml(cell.rowHeaderHtml).length > 0;
                                    const cellValue = cell.value;
                                    if (stripHtml(cellValue).length === 0 && !hasRowHeader) {
                                        return null;
                                    }
                                    return (
                                        <div
                                            key={`rich-column-${column.index}-row-${rowIndex}`}
                                            className={sectionStyles.tableMobileCell}
                                        >
                                            {hasRowHeader ? (
                                                <SitePageHtmlContent
                                                    as="div"
                                                    className={sectionStyles.tableMobileRowHeader}
                                                    html={cell.rowHeaderHtml ?? ''}
                                                />
                                            ) : null}
                                            <SitePageHtmlContent
                                                as="div"
                                                className={sectionStyles.tableMobileCellContent}
                                                html={cellValue}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            ) : null}
            {mobileRows ? (
                <div className={sectionStyles.tableMobile}>
                    {mobileRows.map((row) => (
                        <div
                            key={`rich-row-${row.index}`}
                            className={sectionStyles.tableMobileColumn}
                        >
                            {stripHtml(row.rowHeaderHtml).length > 0 ? (
                                <SitePageHtmlContent
                                    as="div"
                                    className={sectionStyles.tableMobileColumnHeader}
                                    html={row.rowHeaderHtml}
                                />
                            ) : null}
                            {row.cells.map((cellHtml, cellIndex) => {
                                if (stripHtml(cellHtml).length === 0) {
                                    return null;
                                }
                                return (
                                    <div
                                        key={`rich-row-${row.index}-cell-${cellIndex}`}
                                        className={sectionStyles.tableMobileCell}
                                    >
                                        <SitePageHtmlContent
                                            as="div"
                                            className={sectionStyles.tableMobileCellContent}
                                            html={cellHtml}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : null}
            {mobileFallbackRows ? (
                <div className={sectionStyles.tableMobile}>
                    {mobileFallbackRows.map((row) => (
                        <div
                            key={`rich-fallback-row-${row.index}`}
                            className={sectionStyles.tableMobileColumn}
                        >
                            <div className={sectionStyles.tableMobileColumnHeader}>
                                {`Row ${row.index + 1}`}
                            </div>
                            {row.cells.map((cellHtml, cellIndex) => {
                                if (stripHtml(cellHtml).length === 0) {
                                    return null;
                                }
                                return (
                                    <div
                                        key={`rich-fallback-row-${row.index}-cell-${cellIndex}`}
                                        className={sectionStyles.tableMobileCell}
                                    >
                                        <SitePageHtmlContent
                                            as="div"
                                            className={sectionStyles.tableMobileCellContent}
                                            html={cellHtml}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
